// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the symbol set. Each glyph is drawn procedurally from shape primitives (far
// fewer bugs than 60 hand-authored SVG path strings), centred at the origin and sized to
// roughly fill radius `r`. Identity is carried by SILHOUETTE — every glyph is a clearly
// different shape — so the game is fully colour-blind-legible; the accent hue (palette.ts)
// is a redundant cue. The caller sets fillStyle AND strokeStyle to the accent before
// calling, plus lineJoin/lineCap = 'round'. We only need 57 for the densest plane (order
// 7); 60 are defined so there is headroom and no two neighbours look alike.

type Ctx = CanvasRenderingContext2D;
type Glyph = (ctx: Ctx, r: number) => void;

const TAU = Math.PI * 2;

function polyPath(ctx: Ctx, r: number, n: number, rot = -Math.PI / 2): void {
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * TAU;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

function starPath(ctx: Ctx, rOut: number, rIn: number, points: number, rot = -Math.PI / 2): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const a = rot + (i / (points * 2)) * TAU;
    const rr = i % 2 === 0 ? rOut : rIn;
    const x = Math.cos(a) * rr;
    const y = Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

const fillPoly = (n: number, rot?: number): Glyph => (ctx, r) => {
  polyPath(ctx, r, n, rot);
  ctx.fill();
};
const strokePoly = (n: number, rot?: number): Glyph => (ctx, r) => {
  ctx.lineWidth = r * 0.26;
  polyPath(ctx, r, n, rot);
  ctx.stroke();
};
const fillStar = (pts: number, ratio: number, rot?: number): Glyph => (ctx, r) => {
  starPath(ctx, r, r * ratio, pts, rot);
  ctx.fill();
};

function disc(ctx: Ctx, r: number): void {
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
}
function ring(ctx: Ctx, r: number, w = 0.34): void {
  ctx.lineWidth = r * w;
  ctx.beginPath();
  ctx.arc(0, 0, r * (1 - w / 2), 0, TAU);
  ctx.stroke();
}
function dot(ctx: Ctx, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
}

// ── the glyphs ────────────────────────────────────────────────────────────────────────
export const GLYPHS: Glyph[] = [
  // 0 disc
  (ctx, r) => disc(ctx, r * 0.86),
  // 1 ring
  (ctx, r) => ring(ctx, r * 0.9, 0.32),
  // 2 target (three rings)
  (ctx, r) => {
    ring(ctx, r * 0.92, 0.16);
    ring(ctx, r * 0.56, 0.2);
    dot(ctx, 0, 0, r * 0.16);
  },
  // 3 triangle up
  fillPoly(3),
  // 4 triangle down
  fillPoly(3, Math.PI / 2),
  // 5 square
  fillPoly(4, Math.PI / 4),
  // 6 diamond
  fillPoly(4),
  // 7 pentagon
  fillPoly(5),
  // 8 hexagon
  fillPoly(6),
  // 9 octagon
  fillPoly(8, Math.PI / 8),
  // 10 star (5)
  fillStar(5, 0.44),
  // 11 star (6)
  fillStar(6, 0.5),
  // 12 sparkle (4-point)
  fillStar(4, 0.36),
  // 13 burst (8-point)
  fillStar(8, 0.46),
  // 14 star (7)
  fillStar(7, 0.5),
  // 15 pentagram outline
  (ctx, r) => {
    ctx.lineWidth = r * 0.16;
    starPath(ctx, r * 0.92, r * 0.4, 5);
    ctx.stroke();
  },
  // 16 heart
  (ctx, r) => {
    const s = r * 0.98;
    ctx.beginPath();
    ctx.moveTo(0, s * 0.72);
    ctx.bezierCurveTo(-s * 1.15, -s * 0.1, -s * 0.5, -s * 0.95, 0, -s * 0.34);
    ctx.bezierCurveTo(s * 0.5, -s * 0.95, s * 1.15, -s * 0.1, 0, s * 0.72);
    ctx.closePath();
    ctx.fill();
  },
  // 17 teardrop
  (ctx, r) => {
    const s = r * 0.9;
    ctx.beginPath();
    ctx.moveTo(0, -s);
    ctx.bezierCurveTo(s * 0.85, -s * 0.1, s * 0.7, s * 0.9, 0, s * 0.9);
    ctx.bezierCurveTo(-s * 0.7, s * 0.9, -s * 0.85, -s * 0.1, 0, -s);
    ctx.closePath();
    ctx.fill();
  },
  // 18 crescent
  (ctx, r) => {
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.88, 0, TAU);
    ctx.arc(r * 0.5, -r * 0.18, r * 0.78, 0, TAU, true);
    ctx.fill('evenodd');
    ctx.restore();
  },
  // 19 plus
  (ctx, r) => {
    const a = r * 0.32;
    const b = r * 0.9;
    ctx.beginPath();
    ctx.rect(-a, -b, a * 2, b * 2);
    ctx.rect(-b, -a, b * 2, a * 2);
    ctx.fill();
  },
  // 20 saltire (X)
  (ctx, r) => {
    ctx.lineWidth = r * 0.34;
    ctx.beginPath();
    ctx.moveTo(-r * 0.72, -r * 0.72);
    ctx.lineTo(r * 0.72, r * 0.72);
    ctx.moveTo(r * 0.72, -r * 0.72);
    ctx.lineTo(-r * 0.72, r * 0.72);
    ctx.stroke();
  },
  // 21 chevron up
  (ctx, r) => {
    ctx.lineWidth = r * 0.32;
    ctx.beginPath();
    ctx.moveTo(-r * 0.72, r * 0.4);
    ctx.lineTo(0, -r * 0.5);
    ctx.lineTo(r * 0.72, r * 0.4);
    ctx.stroke();
  },
  // 22 double chevron up
  (ctx, r) => {
    ctx.lineWidth = r * 0.22;
    for (const dy of [-r * 0.28, r * 0.26]) {
      ctx.beginPath();
      ctx.moveTo(-r * 0.7, dy + r * 0.34);
      ctx.lineTo(0, dy - r * 0.34);
      ctx.lineTo(r * 0.7, dy + r * 0.34);
      ctx.stroke();
    }
  },
  // 23 arrow up
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.92);
    ctx.lineTo(r * 0.68, -r * 0.05);
    ctx.lineTo(r * 0.26, -r * 0.05);
    ctx.lineTo(r * 0.26, r * 0.9);
    ctx.lineTo(-r * 0.26, r * 0.9);
    ctx.lineTo(-r * 0.26, -r * 0.05);
    ctx.lineTo(-r * 0.68, -r * 0.05);
    ctx.closePath();
    ctx.fill();
  },
  // 24 lightning bolt
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(r * 0.18, -r * 0.95);
    ctx.lineTo(-r * 0.5, r * 0.12);
    ctx.lineTo(-r * 0.04, r * 0.12);
    ctx.lineTo(-r * 0.22, r * 0.95);
    ctx.lineTo(r * 0.5, -r * 0.16);
    ctx.lineTo(r * 0.02, -r * 0.16);
    ctx.closePath();
    ctx.fill();
  },
  // 25 flame
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.95);
    ctx.bezierCurveTo(r * 0.7, -r * 0.3, r * 0.55, r * 0.85, 0, r * 0.9);
    ctx.bezierCurveTo(-r * 0.55, r * 0.85, -r * 0.7, -r * 0.1, -r * 0.05, -r * 0.5);
    ctx.bezierCurveTo(r * 0.05, -r * 0.2, r * 0.24, -r * 0.2, r * 0.16, -r * 0.5);
    ctx.closePath();
    ctx.fill();
  },
  // 26 leaf
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.7, r * 0.7);
    ctx.quadraticCurveTo(-r * 0.2, -r * 0.9, r * 0.75, -r * 0.7);
    ctx.quadraticCurveTo(r * 0.2, r * 0.6, -r * 0.7, r * 0.7);
    ctx.closePath();
    ctx.fill();
  },
  // 27 cloud
  (ctx, r) => {
    ctx.beginPath();
    ctx.arc(-r * 0.45, r * 0.05, r * 0.42, 0, TAU);
    ctx.arc(0, -r * 0.22, r * 0.5, 0, TAU);
    ctx.arc(r * 0.5, r * 0.02, r * 0.4, 0, TAU);
    ctx.rect(-r * 0.45, r * 0.0, r * 0.95, r * 0.45);
    ctx.fill();
  },
  // 28 sun (disc + rays)
  (ctx, r) => {
    ctx.lineWidth = r * 0.16;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r * 0.62, Math.sin(a) * r * 0.62);
      ctx.lineTo(Math.cos(a) * r * 0.95, Math.sin(a) * r * 0.95);
      ctx.stroke();
    }
    dot(ctx, 0, 0, r * 0.42);
  },
  // 29 flower (petals)
  (ctx, r) => {
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU;
      dot(ctx, Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.5, r * 0.34);
    }
  },
  // 30 gear
  (ctx, r) => {
    starPath(ctx, r * 0.95, r * 0.72, 8);
    ctx.fill();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    dot(ctx, 0, 0, r * 0.3);
    ctx.restore();
  },
  // 31 spiral
  (ctx, r) => {
    ctx.lineWidth = r * 0.18;
    ctx.beginPath();
    for (let t = 0; t <= TAU * 2.2; t += 0.2) {
      const rr = (t / (TAU * 2.2)) * r * 0.92;
      const x = Math.cos(t) * rr;
      const y = Math.sin(t) * rr;
      if (t === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  },
  // 32 wave
  (ctx, r) => {
    ctx.lineWidth = r * 0.2;
    ctx.beginPath();
    for (let i = 0; i <= 24; i++) {
      const x = -r * 0.9 + (i / 24) * r * 1.8;
      const y = Math.sin((i / 24) * TAU * 1.5) * r * 0.42;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  },
  // 33 eye
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.92, 0);
    ctx.quadraticCurveTo(0, -r * 0.8, r * 0.92, 0);
    ctx.quadraticCurveTo(0, r * 0.8, -r * 0.92, 0);
    ctx.closePath();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    dot(ctx, 0, 0, r * 0.3);
    ctx.restore();
    ctx.fill();
    dot(ctx, 0, 0, r * 0.24);
  },
  // 34 snowflake
  (ctx, r) => {
    ctx.lineWidth = r * 0.13;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU;
      ctx.save();
      ctx.rotate(a);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -r * 0.92);
      ctx.moveTo(0, -r * 0.5);
      ctx.lineTo(r * 0.28, -r * 0.68);
      ctx.moveTo(0, -r * 0.5);
      ctx.lineTo(-r * 0.28, -r * 0.68);
      ctx.stroke();
      ctx.restore();
    }
  },
  // 35 bowtie
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.8, -r * 0.6);
    ctx.lineTo(0, 0);
    ctx.lineTo(-r * 0.8, r * 0.6);
    ctx.closePath();
    ctx.moveTo(r * 0.8, -r * 0.6);
    ctx.lineTo(0, 0);
    ctx.lineTo(r * 0.8, r * 0.6);
    ctx.closePath();
    ctx.fill();
  },
  // 36 hourglass
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.6, -r * 0.8);
    ctx.lineTo(r * 0.6, -r * 0.8);
    ctx.lineTo(0, 0);
    ctx.lineTo(r * 0.6, r * 0.8);
    ctx.lineTo(-r * 0.6, r * 0.8);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();
  },
  // 37 shield
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.9);
    ctx.lineTo(r * 0.75, -r * 0.55);
    ctx.lineTo(r * 0.62, r * 0.4);
    ctx.quadraticCurveTo(r * 0.3, r * 0.9, 0, r * 0.95);
    ctx.quadraticCurveTo(-r * 0.3, r * 0.9, -r * 0.62, r * 0.4);
    ctx.lineTo(-r * 0.75, -r * 0.55);
    ctx.closePath();
    ctx.fill();
  },
  // 38 house
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.9);
    ctx.lineTo(r * 0.9, -r * 0.05);
    ctx.lineTo(r * 0.62, -r * 0.05);
    ctx.lineTo(r * 0.62, r * 0.8);
    ctx.lineTo(-r * 0.62, r * 0.8);
    ctx.lineTo(-r * 0.62, -r * 0.05);
    ctx.lineTo(-r * 0.9, -r * 0.05);
    ctx.closePath();
    ctx.fill();
  },
  // 39 crown
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.85, r * 0.6);
    ctx.lineTo(-r * 0.85, -r * 0.5);
    ctx.lineTo(-r * 0.4, r * 0.05);
    ctx.lineTo(0, -r * 0.7);
    ctx.lineTo(r * 0.4, r * 0.05);
    ctx.lineTo(r * 0.85, -r * 0.5);
    ctx.lineTo(r * 0.85, r * 0.6);
    ctx.closePath();
    ctx.fill();
  },
  // 40 semicircle
  (ctx, r) => {
    ctx.beginPath();
    ctx.arc(0, r * 0.1, r * 0.9, Math.PI, TAU);
    ctx.closePath();
    ctx.fill();
  },
  // 41 fan / quarter
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(-r * 0.5, r * 0.7);
    ctx.arc(-r * 0.5, r * 0.7, r * 1.4, -Math.PI / 2, 0);
    ctx.closePath();
    ctx.fill();
  },
  // 42 yin-yang-ish (two interlocked commas)
  (ctx, r) => {
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.88, -Math.PI / 2, Math.PI / 2);
    ctx.arc(0, r * 0.44, r * 0.44, Math.PI / 2, -Math.PI / 2, true);
    ctx.arc(0, -r * 0.44, r * 0.44, Math.PI / 2, -Math.PI / 2);
    ctx.closePath();
    ctx.fill();
    dot(ctx, 0, -r * 0.44, r * 0.14);
  },
  // 43 infinity
  (ctx, r) => {
    ctx.lineWidth = r * 0.2;
    ctx.beginPath();
    ctx.arc(-r * 0.42, 0, r * 0.42, 0, TAU);
    ctx.moveTo(r * 0.84, 0);
    ctx.arc(r * 0.42, 0, r * 0.42, 0, TAU);
    ctx.stroke();
  },
  // 44 bone
  (ctx, r) => {
    ctx.lineWidth = r * 0.36;
    ctx.beginPath();
    ctx.moveTo(-r * 0.5, 0);
    ctx.lineTo(r * 0.5, 0);
    ctx.stroke();
    for (const sx of [-1, 1]) {
      dot(ctx, sx * r * 0.62, -r * 0.26, r * 0.24);
      dot(ctx, sx * r * 0.62, r * 0.26, r * 0.24);
    }
  },
  // 45 fish
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(r * 0.5, 0);
    ctx.quadraticCurveTo(0, -r * 0.6, -r * 0.5, 0);
    ctx.quadraticCurveTo(0, r * 0.6, r * 0.5, 0);
    ctx.closePath();
    ctx.moveTo(-r * 0.5, 0);
    ctx.lineTo(-r * 0.9, -r * 0.4);
    ctx.lineTo(-r * 0.9, r * 0.4);
    ctx.closePath();
    ctx.fill();
  },
  // 46 bird (gull)
  (ctx, r) => {
    ctx.lineWidth = r * 0.2;
    ctx.beginPath();
    ctx.moveTo(-r * 0.9, r * 0.2);
    ctx.quadraticCurveTo(-r * 0.4, -r * 0.55, 0, r * 0.1);
    ctx.quadraticCurveTo(r * 0.4, -r * 0.55, r * 0.9, r * 0.2);
    ctx.stroke();
  },
  // 47 clover (trefoil)
  (ctx, r) => {
    for (let i = 0; i < 3; i++) {
      const a = -Math.PI / 2 + (i / 3) * TAU;
      dot(ctx, Math.cos(a) * r * 0.42, Math.sin(a) * r * 0.42, r * 0.42);
    }
  },
  // 48 quatrefoil
  (ctx, r) => {
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU;
      dot(ctx, Math.cos(a) * r * 0.44, Math.sin(a) * r * 0.44, r * 0.4);
    }
  },
  // 49 hex nut (hexagon with a hole)
  (ctx, r) => {
    polyPath(ctx, r * 0.92, 6);
    ctx.fill();
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    dot(ctx, 0, 0, r * 0.38);
    ctx.restore();
  },
  // 50 plus outline
  (ctx, r) => {
    ctx.lineWidth = r * 0.2;
    const a = r * 0.3;
    const b = r * 0.85;
    ctx.beginPath();
    ctx.moveTo(-a, -b);
    ctx.lineTo(a, -b);
    ctx.lineTo(a, -a);
    ctx.lineTo(b, -a);
    ctx.lineTo(b, a);
    ctx.lineTo(a, a);
    ctx.lineTo(a, b);
    ctx.lineTo(-a, b);
    ctx.lineTo(-a, a);
    ctx.lineTo(-b, a);
    ctx.lineTo(-b, -a);
    ctx.lineTo(-a, -a);
    ctx.closePath();
    ctx.stroke();
  },
  // 51 hexagram outline (star of six, lines)
  (ctx, r) => {
    ctx.lineWidth = r * 0.14;
    polyPath(ctx, r * 0.9, 3, -Math.PI / 2);
    ctx.stroke();
    polyPath(ctx, r * 0.9, 3, Math.PI / 2);
    ctx.stroke();
  },
  // 52 pinwheel
  (ctx, r) => {
    for (let i = 0; i < 4; i++) {
      ctx.save();
      ctx.rotate((i / 4) * TAU);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(r * 0.9, -r * 0.05);
      ctx.quadraticCurveTo(r * 0.5, -r * 0.5, 0, 0);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
  },
  // 53 checker (2x2)
  (ctx, r) => {
    const s = r * 0.82;
    ctx.beginPath();
    ctx.rect(-s, -s, s, s);
    ctx.rect(0, 0, s, s);
    ctx.fill();
    ctx.lineWidth = r * 0.1;
    ctx.strokeRect(-s, -s, s * 2, s * 2);
  },
  // 54 bars (three stripes)
  (ctx, r) => {
    const w = r * 0.44;
    for (const x of [-r * 0.62, 0, r * 0.62]) ctx.fillRect(x - w / 2, -r * 0.85, w, r * 1.7);
  },
  // 55 dot grid (2x2)
  (ctx, r) => {
    for (const x of [-r * 0.42, r * 0.42]) for (const y of [-r * 0.42, r * 0.42]) dot(ctx, x, y, r * 0.3);
  },
  // 56 triangle outline
  strokePoly(3),
  // 57 square outline
  strokePoly(4, Math.PI / 4),
  // 58 pentagon outline
  strokePoly(5),
  // 59 kite
  (ctx, r) => {
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.92);
    ctx.lineTo(r * 0.62, 0);
    ctx.lineTo(0, r * 0.92);
    ctx.lineTo(-r * 0.62, 0);
    ctx.closePath();
    ctx.fill();
  },
];

export const GLYPH_COUNT = GLYPHS.length;

/** Draw glyph `id` at the current transform. Caller sets fill/stroke to the accent. */
export function drawGlyph(ctx: Ctx, id: number, r: number): void {
  const g = GLYPHS[id % GLYPHS.length];
  g(ctx, r);
}
